import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useIsAboveMobile from 'components/hooks/useIsAboveMobile';
import { useLocation, useNavigate, useParams } from 'react-router';
import useIsPermitted from 'components/hooks/useIsPermitted';
import { ActionBar } from '@insights-ltd/design-library/components';
import { useBetaEnabled } from 'features';
import { DownloadShareModal } from './DownloadOrshareModal/DownloadOrShareProfileModal';

const addToTeamRoute = 'add-to-team';

type LearnerSelectionBarProps = {
  evaluationIds: string[];
  learnerIds: string[];
  learners?: any;
  teamId?: string;
  setCheckedItems?: (arg: any) => void;
  evaluatorLinkOrganisationId?: string;
};

const LearnerSelectionBar = ({
  evaluationIds,
  learnerIds,
  learners,
  teamId,
  setCheckedItems,
  evaluatorLinkOrganisationId,
}: LearnerSelectionBarProps) => {
  const { isPermitted: canPurchaseProfiles } = useIsPermitted([
    { action: 'PurchaseProfiles', scope: 'Local' },
  ]);

  const { t } = useTranslation();
  const impactProfileEnabled = useBetaEnabled('impact-profile-team-page');
  const aboveMobile = useIsAboveMobile();
  const { evaluatorLinkId } = useParams<{
    evaluatorLinkId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [openDownloadShareModal, setOpenDownloadShareModal] = useState(false);
  const navigateToPurchaseProfiles = () => {
    const navigationState: {
      evaluationIds: string[];
      learners?: any;
      evaluatorLinkId?: string;
      evaluatorLinkOrganisationId?: string;
    } = {
      evaluationIds,
    };

    if (learners) {
      navigationState.learners = learners;
    }

    if (evaluatorLinkId) {
      navigationState.evaluatorLinkId = evaluatorLinkId;
    }

    if (evaluatorLinkOrganisationId) {
      navigationState.evaluatorLinkOrganisationId = evaluatorLinkOrganisationId;
    }

    navigate('purchase-profiles', { state: navigationState });
  };

  const isTeam = location.pathname.includes('/teams');
  const hasActionedAddToTeam = location.pathname.includes(addToTeamRoute);
  const purchaseButton = {
    primaryButtonText: t(
      'ui.event-management.events.purchase-profiles.purchase-button',
    ),
    primaryButtonDisabled: evaluationIds.length === 0 || !canPurchaseProfiles,
    primaryButtonOnClick: navigateToPurchaseProfiles,
  };

  const addToTeamButton = {
    secondaryButtonText: t(
      'ui.event-management.evaluator-links.teams.add-to-team',
    ),
    secondaryButtonDisabled: evaluationIds.length === 0,
    secondaryButtonOnClick: () => navigate(addToTeamRoute),
  };
  const downloadOrShareProfilesButton = {
    secondaryButtonText: t(
      impactProfileEnabled
        ? 'ui.event-management.events.download-or-share-profiles-impact-enabled'
        : 'ui.event-management.events.download-or-share-profiles',
    ),
    secondaryButtonOnClick: () => setOpenDownloadShareModal(true),
  };

  return (
    <>
      {!!evaluationIds.length && !isTeam && !hasActionedAddToTeam && (
        <ActionBar
          barText={t(
            'ui.event-management.events.download-profiles.learners-selected',
            { count: evaluationIds.length },
          )}
          isAboveMobile={aboveMobile}
          primaryButtonObj={purchaseButton}
          secondaryButtonObj={addToTeamButton}
        />
      )}
      {isTeam && !!evaluationIds.length && (
        <>
          <DownloadShareModal
            isOpen={openDownloadShareModal}
            setIsOpen={setOpenDownloadShareModal}
            teamId={teamId || ''}
            learnerIds={learnerIds}
            setCheckedItems={setCheckedItems}
            learners={learners}
          />
          <ActionBar
            barText={t(
              'ui.event-management.events.download-profiles.learners-selected',
              { count: evaluationIds.length },
            )}
            isAboveMobile={aboveMobile}
            secondaryButtonObj={downloadOrShareProfilesButton}
            primaryButtonObj={purchaseButton}
          />
        </>
      )}
    </>
  );
};

export default LearnerSelectionBar;
